import {
  BadIcon,
  BadSolidIcon,
  GoodIcon,
  GoodSolidIcon,
  IconButton,
  Text,
  Tooltip,
  toastService,
} from '@thefarmersfront/kitchen';

import { useDeleteSmartSearchFeedback, usePostSmartSearchFeedback } from '~/hooks/smart-search';
import { useSmartSearchStore } from '~/store/common';

import type { TypeSmartSearchFeedbackState } from '~/types/smart-search.types';

import styles from './SmartSearchFeedback.module.scss';

type SmartSearchFeedbackProps = {
  queryId: string;
};

const SmartSearchFeedback = ({ queryId }: SmartSearchFeedbackProps) => {
  const feedbackState = useSmartSearchStore((s) => s.feedbackState);
  const { setFeedbackState } = useSmartSearchStore((s) => s.actions);

  const { mutate: postFeedback } = usePostSmartSearchFeedback();
  const { mutate: deleteFeedback } = useDeleteSmartSearchFeedback();

  const handleFeedback = (state: TypeSmartSearchFeedbackState) => {
    const prevState = feedbackState;
    const nextState = feedbackState === state ? 'none' : state;
    setFeedbackState(nextState);

    const rollback = () => setFeedbackState(prevState);

    if (nextState === 'none') {
      deleteFeedback(queryId, { onError: rollback });
    } else {
      postFeedback(
        { queryId, helpful: nextState === 'helpful' },
        {
          onError: rollback,
          onSuccess: () => {
            const { hasShownFeedbackToast } = useSmartSearchStore.getState();
            if (!hasShownFeedbackToast) {
              toastService.addToast({ message: '의견을 남겨주셔서 감사합니다.' });
              useSmartSearchStore.setState({ hasShownFeedbackToast: true });
            }
          },
        }
      );
    }
  };

  return (
    <div className={styles['feedback']}>
      <Text size="medium" color="tertiary">
        답변이 도움이 되셨나요?
      </Text>
      <div className={styles['feedback-buttons']}>
        <Tooltip description="도움이 됐어요">
          <IconButton
            icon={
              feedbackState === 'helpful' ? (
                <GoodSolidIcon size={20} color="#BD76FF" />
              ) : feedbackState === 'unhelpful' ? (
                <GoodSolidIcon size={20} color="#ECEFF3" />
              ) : (
                <GoodIcon size={20} color="gray400" />
              )
            }
            onClick={() => handleFeedback('helpful')}
          />
        </Tooltip>
        <Tooltip description="도움이 되지 않았어요" placement="bottom">
          <IconButton
            icon={
              feedbackState === 'unhelpful' ? (
                <BadSolidIcon size={20} color="#BD76FF" />
              ) : feedbackState === 'helpful' ? (
                <BadSolidIcon size={20} color="#ECEFF3" />
              ) : (
                <BadIcon size={20} color="gray400" />
              )
            }
            onClick={() => handleFeedback('unhelpful')}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default SmartSearchFeedback;
